"use client"

import { useState } from "react"
import { ArrowLeft, Mail, Clock, CheckCircle } from "lucide-react"
import { useNavigate } from "react-router-dom"
import TokenChecker from "../TokenChecker"

export default function BetaGate() {
  const navigate = useNavigate()
  const URL_PREFIX = "/api/flowsync/"

  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState("")
  const [existingStatus, setExistingStatus] = useState(null) // 'pending' | 'denied'
  const [showApprovedModal, setShowApprovedModal] = useState(false)
  const [approvedMessage, setApprovedMessage] = useState("You're already approved to use FlowSync.")

  const triggerApprovedFlow = async (message, isConnectedOverride) => {
    setApprovedMessage(message)
    setShowApprovedModal(true)

    let isConnected = isConnectedOverride
    if (typeof isConnected !== "boolean") {
      try {
        const statusRes = await TokenChecker(`${URL_PREFIX}beta/status/`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        })
        if (statusRes.ok) {
          const statusData = await statusRes.json()
          isConnected = !!statusData.is_connected
        }
      } catch {
        isConnected = false
      }
    }

    setTimeout(() => {
      navigate(isConnected ? "/flowsync" : "/flowsync-setup")
    }, 2000)
  }

  const handleRequestAccess = async () => {
    setError("")
    const trimmed = email.trim()
    if (!trimmed) {
      setError("Please enter your email address.")
      return
    }

    setSubmitting(true)
    try {
      const response = await TokenChecker(`${URL_PREFIX}beta/request/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      })

      const data = await response.json()

      if (response.ok || response.status === 201 || response.status === 200) {
        if (data.status === "approved") {
          const message = data.already_approved
            ? "You're already approved to use FlowSync."
            : "You're approved for FlowSync Beta!"
          await triggerApprovedFlow(message, data.is_connected)
          return
        }
        if (data.status === "denied") {
          setExistingStatus("denied")
        } else {
          setSubmitted(true)
        }
      } else {
        setError(data.error || "Something went wrong. Please try again.")
      }
    } catch {
      setError("Network error. Please check your connection and try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showApprovedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="w-[92%] max-w-sm rounded-2xl border border-[#CFE8FB] bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EBF8FF] border border-[#CFE8FB]">
                <CheckCircle className="h-6 w-6 text-[#10A7F3]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{approvedMessage}</p>
                <p className="text-xs text-gray-600 mt-1">Taking you to FlowSync…</p>
              </div>
            </div>
            <div className="mt-5 h-2 w-full rounded-full bg-[#E6F6FF] overflow-hidden">
              <div className="h-full w-full bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] animate-pulse" />
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center justify-center w-10 h-10 rounded-full border border-[#CFE8FB] bg-[#EBF8FF] text-[#4299E1] hover:bg-[#D7EEFF] hover:text-[#3182CE] transition-all duration-200 shadow-sm"
            title="Back to Home"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <span className="text-4xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
              FlowSync
            </span>
            <p className="text-sm text-gray-500">
              AI-powered task extraction and email response generation
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold rounded-full bg-[#FFF3CD] text-[#856404] px-3 py-1 border border-[#FFECB5]">
          Beta
        </span>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-16">
        <div className="bg-white rounded-lg border border-gray-200 p-12">

          {/* Icon */}
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-[#EBF8FF] rounded-xl flex items-center justify-center border border-[#CFE8FB]">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4299E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
          </div>

          {submitted ? (
            /* ── Success state ── */
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <Clock className="w-12 h-12 text-[#4299E1]" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Request received!
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed max-w-md mx-auto mb-6">
                We'll review your request and send you an email at{" "}
                <span className="font-medium text-gray-800">{email.trim()}</span>{" "}
                when access is granted.
              </p>
              <button
                onClick={() => navigate("/home")}
                className="text-sm text-[#4299E1] hover:underline"
              >
                Back to Flowman
              </button>
            </div>
          ) : existingStatus === "denied" ? (
            /* ── Denied state ── */
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Access not available
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed max-w-md mx-auto mb-6">
                Your request was not approved at this time. If you believe this
                is a mistake, please contact the developer.
              </p>
              <button
                onClick={() => navigate("/home")}
                className="text-sm text-[#4299E1] hover:underline"
              >
                Back to Flowman
              </button>
            </div>
          ) : (
            /* ── Default request form ── */
            <>
              <div className="text-center mb-10">
                <h2 className="text-xl font-medium text-gray-900 mb-3">
                  FlowSync is currently in Beta
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed max-w-md mx-auto">
                  This feature is still in early access. You need to explicitly
                  request access from the developer before you can use FlowSync.
                </p>
              </div>

              {/* Email input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter your email — the one you intend to use FlowSync on
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleRequestAccess()}
                    placeholder="you@gmail.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4299E1]/30 focus:border-[#4299E1] transition"
                  />
                </div>
                {error && (
                  <p className="mt-2 text-xs text-red-500">{error}</p>
                )}
              </div>

              {/* Submit button */}
              <button
                onClick={handleRequestAccess}
                disabled={submitting}
                className="w-full bg-[#4299E1] hover:bg-[#3182CE] disabled:opacity-60 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
              >
                {submitting ? "Submitting…" : "Request Access"}
              </button>

              {/* What happens next */}
              <div className="mt-10 pt-8 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900 mb-4">
                  What happens next?
                </h3>
                <div className="space-y-3">
                  {[
                    "Your request is sent to the developer for review.",
                    "Once approved, you'll receive a confirmation email with a link to get started.",
                    "Connect your Gmail and let FlowSync handle your inbox.",
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#EBF8FF] text-[#4299E1] text-xs font-semibold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-600">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
